// Currency helpers used across the dashboard so every money value renders
// in the tenant's preferred ISO 4217 currency.
//
// The tenant's currency is stored on `tenants.currency` (defaulting to USD).
// Every call site does `formatMoney(amount, tenant?.currency)` and the ICU
// `Intl.NumberFormat` handles symbol placement, digit grouping, and locale
// idioms for us — so INR shows "₹1,00,000", NPR shows "NPR 5,000", GBP
// shows "£1,000", etc.

// A curated set of frequently-selected currencies. Every ISO 4217 code is
// still accepted by Intl.NumberFormat if we want to expand later.
export const CURRENCIES = [
  { code: "USD", label: "US Dollar",         symbol: "$",   flag: "🇺🇸" },
  { code: "INR", label: "Indian Rupee",      symbol: "₹",   flag: "🇮🇳" },
  { code: "NPR", label: "Nepalese Rupee",    symbol: "रु",  flag: "🇳🇵" },
  { code: "GBP", label: "British Pound",     symbol: "£",   flag: "🇬🇧" },
  { code: "EUR", label: "Euro",              symbol: "€",   flag: "🇪🇺" },
  { code: "JPY", label: "Japanese Yen",      symbol: "¥",   flag: "🇯🇵" },
  { code: "AUD", label: "Australian Dollar", symbol: "A$",  flag: "🇦🇺" },
  { code: "CAD", label: "Canadian Dollar",   symbol: "C$",  flag: "🇨🇦" },
  { code: "CHF", label: "Swiss Franc",       symbol: "CHF", flag: "🇨🇭" },
  { code: "CNY", label: "Chinese Yuan",      symbol: "¥",   flag: "🇨🇳" },
  { code: "HKD", label: "Hong Kong Dollar",  symbol: "HK$", flag: "🇭🇰" },
  { code: "SGD", label: "Singapore Dollar",  symbol: "S$",  flag: "🇸🇬" },
  { code: "AED", label: "UAE Dirham",        symbol: "د.إ", flag: "🇦🇪" },
  { code: "SAR", label: "Saudi Riyal",       symbol: "﷼",   flag: "🇸🇦" },
  { code: "KWD", label: "Kuwaiti Dinar",     symbol: "د.ك", flag: "🇰🇼" },
  { code: "ZAR", label: "South African Rand",symbol: "R",   flag: "🇿🇦" },
  { code: "BRL", label: "Brazilian Real",    symbol: "R$",  flag: "🇧🇷" },
  { code: "MXN", label: "Mexican Peso",      symbol: "MX$", flag: "🇲🇽" },
  { code: "KRW", label: "South Korean Won",  symbol: "₩",   flag: "🇰🇷" },
  { code: "THB", label: "Thai Baht",         symbol: "฿",   flag: "🇹🇭" },
  { code: "PHP", label: "Philippine Peso",   symbol: "₱",   flag: "🇵🇭" },
  { code: "IDR", label: "Indonesian Rupiah", symbol: "Rp",  flag: "🇮🇩" },
  { code: "MYR", label: "Malaysian Ringgit", symbol: "RM",  flag: "🇲🇾" },
  { code: "VND", label: "Vietnamese Dong",   symbol: "₫",   flag: "🇻🇳" },
  { code: "PKR", label: "Pakistani Rupee",   symbol: "₨",   flag: "🇵🇰" },
  { code: "BDT", label: "Bangladeshi Taka",  symbol: "৳",   flag: "🇧🇩" },
  { code: "LKR", label: "Sri Lankan Rupee",  symbol: "₨",   flag: "🇱🇰" },
  { code: "TRY", label: "Turkish Lira",      symbol: "₺",   flag: "🇹🇷" },
  { code: "RUB", label: "Russian Ruble",     symbol: "₽",   flag: "🇷🇺" },
  { code: "NZD", label: "New Zealand Dollar",symbol: "NZ$", flag: "🇳🇿" },
  { code: "SEK", label: "Swedish Krona",     symbol: "kr",  flag: "🇸🇪" },
  { code: "NOK", label: "Norwegian Krone",   symbol: "kr",  flag: "🇳🇴" },
  { code: "DKK", label: "Danish Krone",      symbol: "kr",  flag: "🇩🇰" },
  { code: "PLN", label: "Polish Złoty",      symbol: "zł",  flag: "🇵🇱" },
  { code: "ILS", label: "Israeli Shekel",    symbol: "₪",   flag: "🇮🇱" },
  { code: "EGP", label: "Egyptian Pound",    symbol: "£",   flag: "🇪🇬" },
  { code: "KES", label: "Kenyan Shilling",   symbol: "KSh", flag: "🇰🇪" },
  { code: "NGN", label: "Nigerian Naira",    symbol: "₦",   flag: "🇳🇬" },
  { code: "ARS", label: "Argentine Peso",    symbol: "$",   flag: "🇦🇷" },
];

const CURRENCY_LOCALE = {
  INR: "en-IN",
  NPR: "en-NP",
  GBP: "en-GB",
  EUR: "en-DE",
  JPY: "ja-JP",
  CNY: "zh-CN",
  KRW: "ko-KR",
  BRL: "pt-BR",
  MXN: "es-MX",
  AED: "ar-AE",
  SAR: "ar-SA",
};

// FX rates cache — populated from `/api/fx/rates` by the FxRatesProvider.
// Consumers call `convertFromUSD(usd, code)` which reads from this cache.
// The BAKED_IN table is only used before the first fetch resolves and as an
// emergency fallback if the provider is down.
const BAKED_IN_USD_FX = {
  USD: 1,      INR: 83,       NPR: 133,      GBP: 0.79,     EUR: 0.92,
  JPY: 156,    AUD: 1.53,     CAD: 1.37,     CHF: 0.88,     CNY: 7.25,
  HKD: 7.83,   SGD: 1.35,     AED: 3.67,     SAR: 3.75,     KWD: 0.31,     ZAR: 18.3,
  BRL: 5.1,    MXN: 17.0,     KRW: 1360,     THB: 36,       PHP: 57,
  IDR: 15900,  MYR: 4.7,      VND: 25000,    PKR: 278,      BDT: 118,
  LKR: 300,    TRY: 32,       RUB: 92,       NZD: 1.67,     SEK: 10.6,
  NOK: 10.7,   DKK: 6.9,      PLN: 4.0,      ILS: 3.6,      EGP: 48,
  KES: 130,    NGN: 1500,     ARS: 1000,
};

// Mutable in-memory cache. Kept simple on purpose — a single object stamped
// with the fetched_at timestamp so the FxRatesProvider can decide when to
// refresh (default: refresh on mount, then every 6h).
let _fxCache = {
  base: "USD",
  rates: { ...BAKED_IN_USD_FX },
  fetched_at: null,
  isLive: false,
};

const _listeners = new Set();

export function setFxRates(payload) {
  if (!payload || !payload.rates) return;
  _fxCache = {
    base: (payload.base || "USD").toUpperCase(),
    rates: payload.rates,
    fetched_at: payload.fetched_at || null,
    provider: payload.provider,
    isLive: true,
  };
  _listeners.forEach((fn) => { try { fn(_fxCache); } catch {} });
}

export function subscribeFxRates(fn) {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
}

export function getFxRates() { return _fxCache; }

/** Convert `amountUSD` into `code` using the live-cached rates. */
export function convertFromUSD(amountUSD, code) {
  const cc = (code || "USD").toUpperCase();
  const rate = _fxCache.rates[cc] ?? BAKED_IN_USD_FX[cc] ?? 1;
  return Number(amountUSD || 0) * rate;
}

export function currencyMeta(code) {
  return CURRENCIES.find((c) => c.code === (code || "USD").toUpperCase()) || CURRENCIES[0];
}

/**
 * Format a numeric amount in the given ISO 4217 currency using Intl.
 * Falls back to a plain formatted number if the code is unknown.
 * @param {number} amount
 * @param {string} [code] - ISO 4217 code (e.g. "USD", "INR"). If omitted,
 *   auto-reads `document.documentElement.dataset.currency` set by AuthContext
 *   at session hydration, so existing `money(x)` call sites Just Work.
 * @param {object} [opts] - Optional Intl overrides. When not provided we let
 *   the ICU per-currency defaults apply so USD/INR/GBP show 2 decimals and
 *   JPY/KRW/VND show 0 (no more "$4.5" / "￥12,345.67" bugs).
 */
export function formatMoney(amount, code, opts = {}) {
  const n = Number(amount || 0);
  let cc = (code || "").toUpperCase();
  if (!cc && typeof document !== "undefined") {
    cc = (document.documentElement.dataset.currency || "USD").toUpperCase();
  }
  cc = cc || "USD";
  const locale = CURRENCY_LOCALE[cc] || "en-US";
  const nfOpts = { style: "currency", currency: cc };
  if (opts.minimumFractionDigits !== undefined) nfOpts.minimumFractionDigits = opts.minimumFractionDigits;
  if (opts.maximumFractionDigits !== undefined) nfOpts.maximumFractionDigits = opts.maximumFractionDigits;
  try {
    return new Intl.NumberFormat(locale, nfOpts).format(n);
  } catch {
    // Unknown code — degrade gracefully.
    const meta = currencyMeta(cc);
    return `${meta.symbol}${n.toLocaleString(undefined, {
      minimumFractionDigits: opts.minimumFractionDigits ?? 2,
      maximumFractionDigits: opts.maximumFractionDigits ?? 2,
    })}`;
  }
}

/** Apply the tenant's currency globally so `formatMoney(x)` (no code) picks
 * it up from anywhere in the app. Called once at auth hydration. */
export function applyTenantCurrency(code) {
  if (typeof document !== "undefined") {
    document.documentElement.dataset.currency = (code || "USD").toUpperCase();
  }
}
